import { createElement } from "react";
import { renderToStaticMarkup } from "react-dom/server";

import HNSColors from "../../../ui library/HNSColors";
import PdfReportDocument from "../../Common/PdfExport/components/PdfReportDocument";
import { getPdfReportBaseStyles } from "../../Common/PdfExport/config/styles";
import SettlementPdfDocument from "../components/pdf/SettlementPdfDocument";

import { buildSettlementPdfAssets, formatPdfCurrency } from "./pdfAssets";
import { getSettlementPdfStyles } from "./pdfStyles";

function getReportStyles() {
  return `${getPdfReportBaseStyles()}
    ${getSettlementPdfStyles()}
    .warning-block {
      background: ${HNSColors.tertiary[900]};
      color: ${HNSColors.primary[500]};
      font-weight: 700;
      padding: 18px 20px;
      border-radius: 12px;
    }`;
}

export async function buildSettlementReportHtmlDocument(documentData, selectedSections = {}) {
  const feeRows = [
    {
      label: "Other Deductions",
      percent: documentData?.feeSchedule?.deduction_percentage,
      color: HNSColors.secondary[400],
    },
    {
      label: "Attorney's Fees",
      percent: documentData?.feeSchedule?.law_firm_percentage,
      color: HNSColors.secondary[500],
    },
    {
      label: "Medical Fees + Fees",
      percent: documentData?.feeSchedule?.medical_percentage,
      color: HNSColors.secondary[600],
    },
    {
      label: "Client Payout",
      percent: documentData?.feeSchedule?.client_percentage,
      color: HNSColors.secondary[800],
    },
  ];
  const settlementAmountPresent =
    documentData?.caseDetail?.settlementAmount !== null ??
    documentData?.caseDetail?.actualTotalSettlementAmount !== null;
  const assets = await buildSettlementPdfAssets(feeRows);
  const markup = renderToStaticMarkup(
    createElement(
      PdfReportDocument,
      { styles: getReportStyles(), title: "Settlement Statement" },
      createElement(SettlementPdfDocument, {
        ...documentData,
        ...assets,
        formatCurrency: formatPdfCurrency,
        selectedSections,
        settlementAmountPresent,
      })
    )
  );

  return `<!DOCTYPE html>${markup}`;
}

export async function openPdfPrintWindow(content) {
  const iframe = document.createElement("iframe");

  iframe.setAttribute("aria-hidden", "true");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  iframe.style.visibility = "hidden";
  iframe.srcdoc = content.replace(/<title>.*?<\/title>/i, "<title></title>");
  document.body.appendChild(iframe);

  const iframeWindow = iframe.contentWindow;

  if (!iframeWindow) {
    iframe.remove();
    throw new Error("Unable to create print frame for PDF export");
  }

  await new Promise((resolve) => {
    const finalize = () => {
      const images = Array.from(iframeWindow.document.images || []);

      return Promise.all(
        images.map((image) =>
          image.complete
            ? Promise.resolve()
            : new Promise((resolve) => {
                image.onload = resolve;
                image.onerror = resolve;
              })
        )
      ).then(() => window.setTimeout(resolve, 250));
    };

    if (iframeWindow.document.readyState === "complete") {
      finalize();
      return;
    }

    iframeWindow.addEventListener("load", finalize, { once: true });
  });

  await new Promise((resolve) => {
    const cleanup = () => {
      iframe.remove();
      resolve();
    };

    iframeWindow.onafterprint = cleanup;
    iframeWindow.focus();
    iframeWindow.print();
    window.setTimeout(cleanup, 1500);
  });
}
